import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Watch, Loader2, CheckCircle2, Unplug, RefreshCw, Smartphone, Activity, Zap, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => localStorage.getItem('annalise_session_id') || '';

const DEVICE_ICONS = {
  apple: Smartphone, ring: Heart, watch: Watch, activity: Activity, zap: Zap,
};

const DeviceManager = ({ isOpen, onClose }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [lastAction, setLastAction] = useState(null);

  const fetchDevices = async () => {
    try {
      const res = await fetch(`${API}/devices/${getSessionId()}`);
      const data = await res.json();
      setDevices(data.devices || []);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) { setLoading(true); fetchDevices(); }
  }, [isOpen]);

  const connectDevice = async (deviceType) => {
    setConnecting(deviceType);
    try {
      const res = await fetch(`${API}/devices/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_type: deviceType, user_session: getSessionId() })
      });
      await res.json();
      await fetchDevices();
      setLastAction(`Connected ${deviceType}`);
      setTimeout(() => setLastAction(null), 2000);
    } catch (err) {
      console.error('Connect failed:', err);
    } finally {
      setConnecting(null);
    }
  };

  const disconnectDevice = async (deviceType) => {
    try {
      await fetch(`${API}/devices/disconnect/${deviceType}?user_session=${getSessionId()}`, { method: 'POST' });
      await fetchDevices();
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  const syncDevice = async (deviceType) => {
    setSyncing(deviceType);
    try {
      const res = await fetch(`${API}/devices/sync/${deviceType}?user_session=${getSessionId()}`, { method: 'POST' });
      const data = await res.json();
      await fetchDevices();
      window.dispatchEvent(new CustomEvent('healthDataUpdated'));
      setLastAction(`Synced: ${data.message}`);
      setTimeout(() => setLastAction(null), 3000);
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(null);
    }
  };

  const syncAll = async () => {
    setSyncAllLoading(true);
    try {
      const res = await fetch(`${API}/devices/sync-all?user_session=${getSessionId()}`, { method: 'POST' });
      const data = await res.json();
      await fetchDevices();
      window.dispatchEvent(new CustomEvent('healthDataUpdated'));
      setLastAction(`Synced ${data.synced_count} device(s)`);
      setTimeout(() => setLastAction(null), 3000);
    } catch (err) {
      console.error('Sync all failed:', err);
    } finally {
      setSyncAllLoading(false);
    }
  };

  const formatLastSync = (ts) => {
    if (!ts) return 'Never';
    try {
      const d = new Date(ts);
      const diff = Date.now() - d.getTime();
      if (diff < 60000) return 'Just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString();
    } catch { return 'Unknown'; }
  };

  if (!isOpen) return null;

  const connectedCount = devices.filter(d => d.connected).length;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="device-manager-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-[#166534] to-[#22c55e]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">Wearable Devices</h2>
              <p className="text-green-100 text-sm">{connectedCount} of {devices.length} connected</p>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="close-device-modal"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Sync All */}
        {connectedCount > 0 && (
          <div className="px-6 py-3 bg-green-50 border-b border-green-100 flex items-center justify-between">
            <span className="text-sm text-[#166534] font-medium">Sync all connected devices</span>
            <Button size="sm" onClick={syncAll} disabled={syncAllLoading}
              className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="sync-all-btn">
              {syncAllLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync All
            </Button>
          </div>
        )}

        {/* Success Banner */}
        {lastAction && (
          <div className="px-6 py-2 bg-green-100 text-green-800 text-sm flex items-center gap-2" data-testid="sync-success-banner">
            <CheckCircle2 className="w-4 h-4" /> {lastAction}
          </div>
        )}

        {/* Device List */}
        <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-[#166534]" /></div>
          ) : (
            devices.map(device => {
              const IconComp = DEVICE_ICONS[device.icon] || Watch;
              return (
                <div key={device.device_type}
                  className={`p-4 rounded-xl border transition-all ${device.connected ? 'bg-white border-green-200 shadow-sm' : 'bg-gray-50 border-gray-200'}`}
                  data-testid={`device-${device.device_type}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${device.connected ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <IconComp className={`w-6 h-6 ${device.connected ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm">{device.name}</h3>
                        {device.connected && <span className="w-2 h-2 bg-green-500 rounded-full" />}
                      </div>
                      <p className="text-xs text-gray-500">
                        {device.connected ? `Last sync: ${formatLastSync(device.last_sync)}` : 'Not connected'}
                      </p>
                      <div className="flex gap-1 mt-1">
                        {device.metrics.map(m => (
                          <span key={m} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{m.replace('_', ' ')}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {device.connected ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => syncDevice(device.device_type)}
                            disabled={syncing === device.device_type}
                            className="border-green-200 text-green-700 hover:bg-green-50 gap-1"
                            data-testid={`sync-${device.device_type}`}>
                            {syncing === device.device_type ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Sync
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => disconnectDevice(device.device_type)}
                            className="text-gray-400 hover:text-red-500" data-testid={`disconnect-${device.device_type}`}>
                            <Unplug className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" onClick={() => connectDevice(device.device_type)}
                          disabled={connecting === device.device_type}
                          className="bg-[#166534] hover:bg-[#14532d] text-white gap-1"
                          data-testid={`connect-${device.device_type}`}>
                          {connecting === device.device_type ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <p className="text-[11px] text-gray-400 text-center">
            Synced data is immediately reflected in your Health Dashboard. Connect devices for automatic tracking.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeviceManager;
